import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, X } from "lucide-react";
import { useState } from "react";

const NAV_ITEMS = [
  { label: "Qui êtes-vous ?", anchor: "personas" },
  { label: "Les visages", anchor: "featured-models" },
  { label: "Concours", anchor: "contests" },
  { label: "Agenda", anchor: "events" },
  { label: "Le manifeste", anchor: "about" },
  { label: "FAQ", anchor: "faq" },
  { label: "Contact", anchor: "contact" },
];

export default function Navbar() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";
  const [mobileOpen, setMobileOpen] = useState(false);

  const goToDashboard = () => {
    setMobileOpen(false);
    if (user?.role === "talent") navigate("/talent");
    else if (user?.role === "creator" || user?.role === "professional") navigate("/createurs");
    else if (user?.role === "admin") navigate("/admin");
    else navigate("/dashboard");
  };

  const scrollOrNavigate = (anchorId) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (onLanding) {
      document.getElementById(anchorId)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${anchorId}`);
    }
  };

  return (
    <nav
      className="sticky top-0 z-40 helencia-navbar"
      data-testid="main-navbar"
    >
      {/* Subtle bronze hairline at the bottom of the navbar */}
      <div className="absolute inset-x-0 bottom-0 h-px" style={{
        background: "linear-gradient(90deg, transparent 0%, rgba(198,142,89,0.35) 20%, rgba(166,111,62,0.55) 50%, rgba(198,142,89,0.35) 80%, transparent 100%)",
      }} />
      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 select-none" data-testid="nav-logo" onClick={() => setMobileOpen(false)} aria-label="Helencia">
          <img src="/helencia-logo-256.png" alt="" width={36} height={36} className="rounded-xl shadow-[0_3px_10px_-2px_rgba(110,75,42,0.18)]" style={{ width: 36, height: 36 }} />
          <span className="font-serif text-2xl font-medium tracking-tight text-[#1A1A1A]">Helencia</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-7 text-[13px] font-medium text-[#1A1A1A]">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.anchor}
              href={`#${item.anchor}`}
              onClick={scrollOrNavigate(item.anchor)}
              className="hover:text-[#C68E59] transition-colors whitespace-nowrap"
              data-testid={`nav-${item.anchor}`}
            >
              {item.label}
            </a>
          ))}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {loading ? null : user ? (
            <>
              <Button
                onClick={goToDashboard}
                className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-5 h-10 text-sm hidden sm:inline-flex"
                data-testid="nav-dashboard-btn"
              >
                Mon dashboard
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger className="focus:outline-none">
                  <Avatar className="h-9 w-9 border border-[#D4C3A5] cursor-pointer">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-[#F5E6D0] text-[#1A1A1A] font-semibold">
                      {user.name?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 rounded-xl">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold">{user.name}</p>
                    <p className="text-xs text-[#8B8680]">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={goToDashboard} data-testid="nav-menu-dashboard">Mon dashboard</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="nav-menu-logout">Déconnexion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden sm:inline-flex text-sm font-medium text-[#1A1A1A] hover:text-[#C68E59] transition-colors px-3"
                data-testid="nav-login-btn"
              >
                Connexion
              </Link>
              <Link to="/register">
                <Button
                  className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-5 h-10 text-sm hidden sm:inline-flex"
                  data-testid="nav-signup-btn"
                >
                  Créer un compte
                </Button>
              </Link>
            </>
          )}
          {/* Mobile burger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden ml-2 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#FAF3EA] transition"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5 text-[#1A1A1A]" /> : <Menu className="w-5 h-5 text-[#1A1A1A]" />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#EADDC9] bg-white" data-testid="nav-mobile-sheet">
          <div className="px-6 py-5 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.anchor}
                href={`#${item.anchor}`}
                onClick={scrollOrNavigate(item.anchor)}
                className="py-3 text-[15px] text-[#1A1A1A] border-b border-[#FAF3EA]"
              >
                {item.label}
              </a>
            ))}
            {!user && (
              <div className="flex gap-2 mt-4">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full border-[#1A1A1A] h-11">Connexion</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1">
                  <Button className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-11">Créer un compte</Button>
                </Link>
              </div>
            )}
            {user && (
              <Button onClick={goToDashboard} className="mt-4 w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-11">
                Mon dashboard
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
